import { computed } from 'vue';
import { CampaignType, MinimalOrganisationDto } from '@studyportals/campaign-management-api-interface';
import { IRouter, Routes } from '@/models/router/router.interface';
import { InternalNavigationItem, ExternalNavigationItem } from '@/models/navigation.interface';
import { NotificationStatuses } from '@/models/notifications.interface';
import campaignReadinessPresenter from '@/presenters/campaign-readiness-presenter';
import navigationPresenter from '@/presenters/navigation-presenter';
import store from '@/store';

export default class NavigationBarCode {
	public internalNavItems = computed((): InternalNavigationItem[] => {
		const shouldListingCampaignRelatedLinkBeDisabled = this.isCreationInProgress.value
			|| this.isSaveInProgress.value
			|| this.isCustomProductsCampaign.value
			|| this.isCampaignCreation.value;

		const shouldStandardLinkBeDisabled = this.isCreationInProgress.value
			|| this.isSaveInProgress.value
			|| this.isCampaignCreation.value;

		return [
			{
				label: 'Campaign Settings',
				icon: 'lnr-cog',
				path: Routes.GENERAL,
				disabled: this.isCreationInProgress.value || this.isSaveInProgress.value,
				disabledLabel: `Campaign Settings is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			},
			{
				label: 'Linked Organisations',
				icon: 'lnr-apartment',
				path: Routes.LINKED_ORGANISATIONS,
				disabled: shouldListingCampaignRelatedLinkBeDisabled,
				disabledLabel: `Linked Organisations is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			},
			{
				label: 'Entities & Geotargeting',
				icon: 'lnr-earth',
				path: Routes.ENTITIES_AND_GEOTARGETING,
				disabled: shouldListingCampaignRelatedLinkBeDisabled,
				disabledLabel: `Entities & Geotargeting is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			},
			{
				label: 'Custom Products',
				icon: 'lnr-plus',
				path: Routes.CUSTOM_PRODUCTS,
				disabled: shouldStandardLinkBeDisabled,
				disabledLabel: `Custom Products is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			},
			{
				label: 'Rights',
				icon: 'lnr-users2',
				path: Routes.RIGHTS,
				disabled: shouldStandardLinkBeDisabled,
				disabledLabel: `Rights is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			},
			{
				label: 'Internal Notes',
				icon: 'lnr-clipboard-text',
				path: Routes.INTERNAL_NOTES,
				disabled: shouldStandardLinkBeDisabled,
				disabledLabel: `Internal notes is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			},
			{
				label: 'Auditor',
				icon: 'lnr-list2',
				path: Routes.AUDITOR,
				disabled: shouldStandardLinkBeDisabled,
				disabledLabel: `Auditor is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			}
		];
	});

	public externalNavItems = computed((): ExternalNavigationItem[] => {
		let mainOrganisation: MinimalOrganisationDto | undefined;
		if (this.linkedOrganisations.value.length) {
			mainOrganisation = this.linkedOrganisations.value[0];
		}

		return [
			{
				label: 'My Dashboard',
				icon: 'lnr-home2',
				path: `${window.location.origin}/organisation/`,
				disabled: this.isCreationInProgress.value || this.isSaveInProgress.value,
				disabledLabel: `My Dashboard is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			},
			{
				label: 'Campaign Overview',
				icon: 'lnr-align-bottom',
				path: `${window.location.origin}/statistics/campaigns/`,
				disabled: this.isCreationInProgress.value || this.isSaveInProgress.value,
				disabledLabel: `Campaign Overview is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			},
			{
				label: 'Organisation Editor',
				icon: 'lnr-apartment',
				path: mainOrganisation ? `${window.location.origin}/organisations/edit/${mainOrganisation.id}/editor.html` : '#',
				disabled: this.isCreationInProgress.value || this.isSaveInProgress.value || mainOrganisation === undefined,
				disabledLabel: this.isCreationInProgress.value || this.isSaveInProgress.value
					? `Organisation Editor is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
					: 'This link only becomes available when at least one organisation is linked to the campaign'
			},
			{
				label: 'Manage Users',
				icon: 'lnr-user-plus',
				path: `${window.location.origin}/users-management/`,
				disabled: this.isCreationInProgress.value || this.isSaveInProgress.value,
				disabledLabel: `Manage Users is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			},
			{
				label: 'How to use the Editor',
				icon: 'lnr-lamp',
				path: 'https://studyportals.talentlms.com/plus/catalog/courses/971',
				disabled: this.isCreationInProgress.value || this.isSaveInProgress.value,
				disabledLabel: `This link is disabled ${this.contextSpecificPartOfDisabledNavItemLabel.value}`
			}
		];
	});

	private contextSpecificPartOfDisabledNavItemLabel = computed((): string => {
		if (this.isCreationInProgress.value) {
			return 'while creating a campaign';
		}

		if (this.isSaveInProgress.value) {
			return 'while saving a campaign';
		}

		if (this.isCampaignCreation.value) {
			return 'for newly created campaigns';
		}

		if (this.isCustomProductsCampaign.value) {
			return 'for custom-product-only campaigns';
		}

		return '';
	});

	private router = computed((): IRouter => {
		return store.getters.router();
	});

	private linkedOrganisations = computed((): MinimalOrganisationDto[] => {
		return store.getters.linkedOrganisations();
	});

	private isCustomProductsCampaign = computed((): boolean => {
		return this.campaignType.value === CampaignType.CUSTOM_PRODUCTS;
	});

	private campaignType = computed((): CampaignType | null => {
		return store.getters.campaignType();
	});

	private isCampaignCreation = computed((): boolean => {
		return store.getters.isCampaignCreation();
	});

	private isSaveInProgress = computed((): boolean => {
		return this.notificationStatus.value === NotificationStatuses.SAVING_IN_PROGRESS;
	});

	private isCreationInProgress = computed((): boolean => {
		return this.notificationStatus.value === NotificationStatuses.CREATION_IN_PROGRESS;
	});

	private notificationStatus = computed((): NotificationStatuses => {
		return store.getters.notificationStatus();
	});

	public isPathActive(path: Routes): boolean {
		return this.router.value.current === path;
	}

	public doesPageRequireAction(path: Routes): boolean {
		return campaignReadinessPresenter.doesPageRequireAction(path);
	}

	public navigateToPage(path: Routes, disabled: boolean): void {
		if (disabled) {
			return;
		}

		navigationPresenter.navigateToPage(path);
	}
}
